import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Grid,
  Stack,
  InputAdornment,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Collapse,
  Tooltip,
} from "@mui/material";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";

// REDUX ACTIONS
import {
  getProductAttributeList,
  createProductAttribute,
  updateProductAttribute,
  deleteProductAttribute,
} from "../../../../../store/products/action";
import DeleteModal from "../../../../../Components/Common/DeleteModal";

// TOAST
import { toast } from "react-toastify";

// Search Highlighter Component
const HighlightedText = ({ text = "", highlight = "" }) => {
  if (!highlight.trim() || !text) return <span>{text}</span>;
  const regex = new RegExp(`(${highlight})`, "gi");
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "#fff59d",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

export default function AttributesSettings() {
  const theme = useTheme();
  const dispatch = useDispatch();

  // --- REDUX STATE (Added createUpdateLoading) ---
  const { productAttributeList, productAttributeListLoading } = useSelector(
    (state) => ({
      productAttributeList:
        state.Products.productAttributeList?.productattribute_list,
      productAttributeListLoading: state.Products.productAttributeListLoading,
    })
  );
  const {
    createProductAttributeSuccess,
    updateProductAttributeSuccess,
    deleteProductAttributeSuccess,
    createUpdateLoading,
  } = useSelector((state) => state.Products);

  // Local UI States
  const [exampleBasePrice, setExampleBasePrice] = useState(3.5);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTabs, setExpandedTabs] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [visibleSchedules, setVisibleSchedules] = useState([]);

  // Tracks exactly what we are waiting for the API to do ('create', 'update', or 'delete')
  const [awaitingAction, setAwaitingAction] = useState(null);

  // Delete Modal States
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Local Data State
  const [attributes, setAttributes] = useState([]);

  // --- INIT & SYNC DATA ---
  useEffect(() => {
    dispatch(getProductAttributeList());
  }, [dispatch]);

  useEffect(() => {
    if (productAttributeList && productAttributeList.length > 0) {
      setAttributes(productAttributeList);
    }
  }, [productAttributeList]);

  // --- 1. TOAST & SYNC: CREATE ACTION ---
  useEffect(() => {
    // Only check when the loading cycle finishes for a create action
    if (awaitingAction === "create" && createUpdateLoading === false) {
      if (createProductAttributeSuccess?.status === 200) {
        toast.success(
          createProductAttributeSuccess.message || "Created Successfully"
        );

        const realDatabaseId =
          createProductAttributeSuccess.ProductAttribute_Id?.toString() ||
          Date.now().toString();
        setAttributes((prev) =>
          prev.map((attr) =>
            attr.Id === savingId ? { ...attr, Id: realDatabaseId } : attr
          )
        );
        setExpandedTabs((prev) =>
          prev.map((id) => (id === savingId ? realDatabaseId : id))
        );
      } else if (createProductAttributeSuccess?.status) {
        // Ensures we don't accidentally fire on the initial empty array [] state
        toast.error(
          createProductAttributeSuccess.message || "Failed to create attribute"
        );
      }

      setAwaitingAction(null);
      setSavingId(null);
    }
  }, [createUpdateLoading]); // Triggered only when loading state changes

  // --- 2. TOAST & SYNC: UPDATE ACTION ---
  useEffect(() => {
    // Only check when the loading cycle finishes for an update action
    if (awaitingAction === "update" && createUpdateLoading === false) {
      if (updateProductAttributeSuccess?.status === 200) {
        toast.success(
          updateProductAttributeSuccess.message || "Updated Successfully"
        );
      } else if (updateProductAttributeSuccess?.status) {
        toast.error(
          updateProductAttributeSuccess.message || "Failed to update attribute"
        );
      }

      setAwaitingAction(null);
      setSavingId(null);
    }
  }, [createUpdateLoading]);

  // --- 3. TOAST & SYNC: DELETE ACTION ---
  useEffect(() => {
    // Delete doesn't have a loading flag in Redux, so we listen to the success object changing
    if (awaitingAction === "delete") {
      if (deleteProductAttributeSuccess?.status === 200) {
        toast.success(
          deleteProductAttributeSuccess.message || "Deleted Successfully"
        );

        // ONLY remove from UI if the server confirms the deletion
        if (pendingDeleteId) {
          setAttributes((prev) =>
            prev.filter((attr) => attr.Id !== pendingDeleteId)
          );
        }

        setAwaitingAction(null);
        setPendingDeleteId(null);
      } else if (deleteProductAttributeSuccess?.status) {
        toast.error(
          deleteProductAttributeSuccess.message || "Failed to delete attribute"
        );
        setAwaitingAction(null);
        setPendingDeleteId(null);
      }
    }
  }, [deleteProductAttributeSuccess]);

  // --- SEARCH FILTER ---
  const filteredAttributes = useMemo(() => {
    if (!searchQuery.trim()) return attributes;
    const query = searchQuery.toLowerCase();
    return attributes.filter((attr) => {
      const matchName = attr.Name?.toLowerCase().includes(query);
      const matchOptions = attr.productattributevalue_list?.some((opt) =>
        opt.Value?.toLowerCase().includes(query)
      );
      return matchName || matchOptions;
    });
  }, [attributes, searchQuery]);

  // --- UI TOGGLES ---
  const handleToggleAccordion = (panelId) => {
    if (panelId.startsWith("temp-")) return;
    setExpandedTabs((prev) =>
      prev.includes(panelId)
        ? prev.filter((id) => id !== panelId)
        : [...prev, panelId]
    );
  };
  const handleExpandAll = () =>
    setExpandedTabs(filteredAttributes.map((attr) => attr.Id));
  const handleCollapseAll = () => setExpandedTabs([]);
  const handleToggleScheduleView = (id) =>
    setVisibleSchedules((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );

  // --- STATE MODIFICATION HANDLERS ---
  const handleCreatePlaceholder = () => {
    const tempId = `temp-${Date.now()}`;
    const newAttribute = {
      Id: tempId,
      Name: "New Attribute",
      Code: "",
      FlagRequired: null,
      FlagAllowMulipleChoices: null,
      Description: "",
      StartDateTime: "",
      EndDateTime: "",
      productattributevalue_list: [],
    };
    setAttributes((prev) => [newAttribute, ...prev]);
    setExpandedTabs((prev) => [...prev, tempId]);
  };

  const handleUpdateAttribute = (attrId, field, val) =>
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId ? { ...attr, [field]: val } : attr
      )
    );

  const handleAddOption = (attrId) => {
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list: [
                ...attr.productattributevalue_list,
                {
                  Id: `temp-opt-${Date.now()}`,
                  ProductAttribute_Id: attrId,
                  Value: "",
                  Code: "",
                  FlagDefault: null,
                  StartDateTime: "",
                  EndDateTime: "",
                  productattributevalueprice_list: [],
                },
              ],
            }
          : attr
      )
    );
  };

  const handleUpdateOption = (attrId, optId, field, val) =>
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list: attr.productattributevalue_list.map(
                (opt) => (opt.Id === optId ? { ...opt, [field]: val } : opt)
              ),
            }
          : attr
      )
    );

  const handleDeleteOption = (attrId, optId) =>
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list:
                attr.productattributevalue_list.filter(
                  (opt) => opt.Id !== optId
                ),
            }
          : attr
      )
    );

  const handleSetDefaultOption = (attrId, optId) =>
    setAttributes(
      attributes.map((attr) => {
        if (attr.Id === attrId) {
          const isMulti = attr.FlagAllowMulipleChoices === "1";
          return {
            ...attr,
            productattributevalue_list: attr.productattributevalue_list.map(
              (opt) => ({
                ...opt,
                FlagDefault:
                  opt.Id === optId
                    ? isMulti
                      ? opt.FlagDefault === "1"
                        ? null
                        : "1"
                      : "1"
                    : isMulti
                    ? opt.FlagDefault
                    : null,
              })
            ),
          };
        }
        return attr;
      })
    );

  // --- NEW PRICE TIER HANDLERS ---
  const handleAddPrice = (attrId, optId) => {
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list: attr.productattributevalue_list.map(
                (opt) =>
                  opt.Id === optId
                    ? {
                        ...opt,
                        productattributevalueprice_list: [
                          ...(opt.productattributevalueprice_list || []),
                          {
                            Id: `temp-price-${Date.now()}`,
                            AmountAdjustmentType: "price-add",
                            AmountLocalSubTotal: "0.00",
                            AmountLocalVat: "0.00",
                            AmountLocalTotal: "0.00",
                            AmountVatPercentage: null,
                            StartDateTime: "",
                            EndDateTime: "",
                          },
                        ],
                      }
                    : opt
              ),
            }
          : attr
      )
    );
    setVisibleSchedules((prev) => [...prev, `opt-prices-${optId}`]);
  };

  const handleUpdatePrice = (attrId, optId, priceId, field, val) => {
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list: attr.productattributevalue_list.map(
                (opt) =>
                  opt.Id === optId
                    ? {
                        ...opt,
                        productattributevalueprice_list:
                          opt.productattributevalueprice_list.map((price) =>
                            price.Id === priceId
                              ? {
                                  ...price,
                                  [field]: val,
                                  AmountLocalTotal:
                                    field === "AmountLocalSubTotal"
                                      ? val
                                      : price.AmountLocalTotal,
                                }
                              : price
                          ),
                      }
                    : opt
              ),
            }
          : attr
      )
    );
  };

  const handleDeletePrice = (attrId, optId, priceId) => {
    setAttributes(
      attributes.map((attr) =>
        attr.Id === attrId
          ? {
              ...attr,
              productattributevalue_list: attr.productattributevalue_list.map(
                (opt) =>
                  opt.Id === optId
                    ? {
                        ...opt,
                        productattributevalueprice_list:
                          opt.productattributevalueprice_list.filter(
                            (price) => price.Id !== priceId
                          ),
                      }
                    : opt
              ),
            }
          : attr
      )
    );
  };

  // --- SAVE ACTION ---
  const handleSaveAttribute = (attrId) => {
    setSavingId(attrId);
    const rawAttr = attributes.find((a) => a.Id === attrId);
    const isNewRecord = attrId.startsWith("temp-");

    const cleanedOptions = rawAttr.productattributevalue_list.map((opt) => {
      const cleanPrices = (opt.productattributevalueprice_list || []).map(
        (price) => ({
          ...(price.Id?.startsWith("temp-") ? {} : { Id: price.Id }),
          AmountAdjustmentType: price.AmountAdjustmentType,
          AmountLocalSubTotal: price.AmountLocalSubTotal?.toString(),
          AmountLocalVat: price.AmountLocalVat?.toString() || "0.00",
          AmountLocalTotal:
            price.AmountLocalTotal?.toString() ||
            price.AmountLocalSubTotal?.toString(),
          AmountVatPercentage: price.AmountVatPercentage,
          StartDateTime: price.StartDateTime || null,
          EndDateTime: price.EndDateTime || null,
        })
      );

      return {
        ...(opt.Id?.startsWith("temp-") ? {} : { Id: opt.Id }),
        Value: opt.Value,
        Code: opt.Code,
        FlagDefault: opt.FlagDefault,
        StartDateTime: opt.StartDateTime || null,
        EndDateTime: opt.EndDateTime || null,
        productattributevalueprice_list: cleanPrices,
      };
    });

    const payload = {
      Name: rawAttr.Name,
      Code: rawAttr.Code,
      Description: rawAttr.Description,
      FlagRequired: rawAttr.FlagRequired,
      FlagAllowMulipleChoices: rawAttr.FlagAllowMulipleChoices,
      StartDateTime: rawAttr.StartDateTime || null,
      EndDateTime: rawAttr.EndDateTime || null,
      productattributevalue_list: cleanedOptions,
    };

    if (isNewRecord) {
      setAwaitingAction("create");
      dispatch(createProductAttribute([payload]));
    } else {
      setAwaitingAction("update");
      payload.Id = rawAttr.Id;
      dispatch(updateProductAttribute(payload));
    }
  };

  // --- DELETE MODAL ---
  const handleOpenDeleteModal = (attrId, event) => {
    event.stopPropagation();
    setAttributeToDelete(attrId);
    setDeleteModalShow(true);
  };

  const handleConfirmDelete = () => {
    if (attributeToDelete) {
      if (!attributeToDelete.startsWith("temp-")) {
        setAwaitingAction("delete");
        setPendingDeleteId(attributeToDelete);
        dispatch(deleteProductAttribute([attributeToDelete]));
      } else {
        toast.info("Unsaved attribute removed");
        setAttributes(
          attributes.filter((attr) => attr.Id !== attributeToDelete)
        );
      }
    }
    setDeleteModalShow(false);
    setAttributeToDelete(null);
  };

  // --- PREVIEW CALCULATOR ---
  const calculatePreview = (type, valueStr) => {
    const val = parseFloat(valueStr) || 0;
    let result = exampleBasePrice;
    if (type === "price-add") result += val;
    if (type === "price-minus") result -= val;
    if (type === "percentage-add") result += result * (val / 100);
    if (type === "percentage-minus") result -= result * (val / 100);
    return `£${Math.max(0, result).toFixed(2)}`;
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        p: { xs: 1, sm: 3 },
        pb: { xs: 10, sm: 15 },
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "center",
          p: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 2 }}
        >
          <Typography variant="h5" fontWeight={800} color="primary.dark">
            Attributes Settings
          </Typography>
          {productAttributeListLoading && <CircularProgress size={20} />}
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Example Base Price"
            type="number"
            size="small"
            value={exampleBasePrice}
            onChange={(e) => setExampleBasePrice(Number(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
            }}
            sx={{ width: 140, bgcolor: "background.paper" }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddCircleRoundedIcon />}
            onClick={handleCreatePlaceholder}
            sx={{
              borderRadius: 8,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Add Attribute
          </Button>
        </Box>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          placeholder="Search attributes or options..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 400, bgcolor: "background.paper" }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            startIcon={<UnfoldMoreRoundedIcon />}
            onClick={handleExpandAll}
            color="inherit"
          >
            Expand All
          </Button>
          <Button
            size="small"
            startIcon={<UnfoldLessRoundedIcon />}
            onClick={handleCollapseAll}
            color="inherit"
          >
            Collapse All
          </Button>
        </Box>
      </Box>

      {/* Attributes List */}
      <Stack spacing={2}>
        {filteredAttributes.map((attr) => {
          const isExpanded = expandedTabs.includes(attr.Id);
          const isNewRecord = attr.Id.startsWith("temp-");
          const isCurrentlySaving = savingId === attr.Id;
          const showAttrSchedule =
            visibleSchedules.includes(`attr-sch-${attr.Id}`) ||
            attr.StartDateTime ||
            attr.EndDateTime;

          const isNameEmpty = !attr.Name || attr.Name.trim() === "";
          const hasNoOptions =
            !attr.productattributevalue_list ||
            attr.productattributevalue_list.length === 0;
          const isSaveDisabled =
            isCurrentlySaving || isNameEmpty || hasNoOptions;

          return (
            <Accordion
              key={attr.Id}
              expanded={isExpanded}
              onChange={() => handleToggleAccordion(attr.Id)}
              elevation={0}
              sx={{
                border: `1px solid ${
                  isNewRecord
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: isExpanded
                    ? alpha(theme.palette.primary.main, 0.03)
                    : "transparent",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    pr: 1,
                    gap: 2,
                  }}
                >
                  <LocalOfferOutlinedIcon
                    sx={{
                      color: isNewRecord ? "primary.main" : "text.secondary",
                      opacity: 0.7,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ minWidth: 100 }}
                  >
                    <HighlightedText
                      text={attr.Name || "Unnamed"}
                      highlight={searchQuery}
                    />
                  </Typography>

                  {!isExpanded && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flexGrow: 1 }}
                    >
                      {attr.productattributevalue_list?.length || 0} options
                      {(attr.StartDateTime || attr.EndDateTime) &&
                        " • 📅 Scheduled"}
                    </Typography>
                  )}
                  {isExpanded && <Box sx={{ flexGrow: 1 }} />}

                  {isNewRecord && !isExpanded && (
                    <Chip
                      size="small"
                      label="Unsaved"
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 2 }}
                    />
                  )}

                  {/* Disable delete button while an API call is running on it */}
                  <IconButton
                    size="small"
                    color="error"
                    disabled={
                      awaitingAction === "delete" && pendingDeleteId === attr.Id
                    }
                    onClick={(e) => handleOpenDeleteModal(attr.Id, e)}
                  >
                    {awaitingAction === "delete" &&
                    pendingDeleteId === attr.Id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DeleteRoundedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}
                >
                  {/* ATTRIBUTE HEADER FIELDS */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={attr.Name || ""}
                        onChange={(e) =>
                          handleUpdateAttribute(attr.Id, "Name", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Code"
                        value={attr.Code || ""}
                        onChange={(e) =>
                          handleUpdateAttribute(attr.Id, "Code", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Selection Type"
                        value={
                          attr.FlagAllowMulipleChoices === "1"
                            ? "multiple"
                            : "single"
                        }
                        onChange={(e) =>
                          handleUpdateAttribute(
                            attr.Id,
                            "FlagAllowMulipleChoices",
                            e.target.value === "multiple" ? "1" : null
                          )
                        }
                      >
                        <MenuItem value="single">Single Select</MenuItem>
                        <MenuItem value="multiple">Multi Select</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={attr.FlagRequired === "1"}
                            onChange={(e) =>
                              handleUpdateAttribute(
                                attr.Id,
                                "FlagRequired",
                                e.target.checked ? "1" : null
                              )
                            }
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={600}>
                            Required
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={showAttrSchedule}
                            onChange={() =>
                              handleToggleScheduleView(`attr-sch-${attr.Id}`)
                            }
                            color="secondary"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={600}>
                            Schedule
                          </Typography>
                        }
                      />
                    </Grid>
                  </Grid>

                  {/* ATTRIBUTE SCHEDULE */}
                  <Collapse in={showAttrSchedule}>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                        borderRadius: 2,
                        border: `1px dashed ${theme.palette.secondary.light}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="secondary.dark"
                        display="block"
                        gutterBottom
                      >
                        PROMOTION / SCHEDULING (ATTRIBUTE LEVEL)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            label="Start Date & Time"
                            value={attr.StartDateTime || ""}
                            onChange={(e) =>
                              handleUpdateAttribute(
                                attr.Id,
                                "StartDateTime",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="datetime-local"
                            label="End Date & Time"
                            value={attr.EndDateTime || ""}
                            onChange={(e) =>
                              handleUpdateAttribute(
                                attr.Id,
                                "EndDateTime",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>

                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Description / Internal Note"
                    value={attr.Description || ""}
                    onChange={(e) =>
                      handleUpdateAttribute(
                        attr.Id,
                        "Description",
                        e.target.value
                      )
                    }
                    sx={{ mb: 3 }}
                  />

                  {/* OPTIONS LIST */}
                  <Stack spacing={2}>
                    {attr.productattributevalue_list?.map((opt) => {
                      const showOptSchedule =
                        visibleSchedules.includes(`opt-sch-${opt.Id}`) ||
                        opt.StartDateTime ||
                        opt.EndDateTime;
                      const showPrices =
                        visibleSchedules.includes(`opt-prices-${opt.Id}`) ||
                        opt.productattributevalueprice_list?.length > 0;

                      return (
                        <Box
                          key={opt.Id}
                          sx={{
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            transition: "all 0.2s",
                          }}
                        >
                          {/* OPTION MAIN ROW */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <TextField
                              size="small"
                              label="Option Name"
                              value={opt.Value || ""}
                              sx={{ flex: 1, minWidth: 200 }}
                              onChange={(e) =>
                                handleUpdateOption(
                                  attr.Id,
                                  opt.Id,
                                  "Value",
                                  e.target.value
                                )
                              }
                            />
                            <TextField
                              size="small"
                              label="Code"
                              value={opt.Code || ""}
                              sx={{ width: 100 }}
                              onChange={(e) =>
                                handleUpdateOption(
                                  attr.Id,
                                  opt.Id,
                                  "Code",
                                  e.target.value
                                )
                              }
                            />

                            <Button
                              variant={
                                opt.FlagDefault === "1"
                                  ? "contained"
                                  : "outlined"
                              }
                              color={
                                opt.FlagDefault === "1" ? "primary" : "inherit"
                              }
                              size="small"
                              onClick={() =>
                                handleSetDefaultOption(attr.Id, opt.Id)
                              }
                              sx={{
                                minWidth: 100,
                                textTransform: "none",
                                borderRadius: 6,
                                boxShadow: "none",
                              }}
                            >
                              {opt.FlagDefault === "1"
                                ? "Default"
                                : "Set default"}
                            </Button>

                            <Tooltip title="Schedule Option">
                              <IconButton
                                size="small"
                                color={
                                  showOptSchedule ? "secondary" : "default"
                                }
                                onClick={() =>
                                  handleToggleScheduleView(`opt-sch-${opt.Id}`)
                                }
                              >
                                {showOptSchedule ? (
                                  <EventAvailableRoundedIcon fontSize="small" />
                                ) : (
                                  <EventRoundedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>

                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteOption(attr.Id, opt.Id)
                              }
                            >
                              <DeleteRoundedIcon
                                fontSize="small"
                                color="error"
                              />
                            </IconButton>
                          </Box>

                          {/* OPTION SCHEDULE */}
                          <Collapse in={showOptSchedule}>
                            <Box
                              sx={{
                                mt: 2,
                                p: 1.5,
                                bgcolor: alpha(
                                  theme.palette.secondary.main,
                                  0.03
                                ),
                                borderRadius: 1,
                                borderLeft: `2px solid ${theme.palette.secondary.light}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="secondary.main"
                                display="block"
                                sx={{ mb: 1 }}
                              >
                                OPTION SCHEDULE
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="datetime-local"
                                    label="Option Start Time"
                                    value={opt.StartDateTime || ""}
                                    onChange={(e) =>
                                      handleUpdateOption(
                                        attr.Id,
                                        opt.Id,
                                        "StartDateTime",
                                        e.target.value
                                      )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    type="datetime-local"
                                    label="Option End Time"
                                    value={opt.EndDateTime || ""}
                                    onChange={(e) =>
                                      handleUpdateOption(
                                        attr.Id,
                                        opt.Id,
                                        "EndDateTime",
                                        e.target.value
                                      )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>

                          {/* OPTION PRICES SUB-LIST */}
                          <Collapse in={showPrices}>
                            <Stack
                              spacing={1}
                              sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: `1px dashed ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                              >
                                PRICE MODIFIERS
                              </Typography>

                              {opt.productattributevalueprice_list?.map(
                                (price) => {
                                  const showPriceSchedule =
                                    visibleSchedules.includes(
                                      `price-sch-${price.Id}`
                                    ) ||
                                    price.StartDateTime ||
                                    price.EndDateTime;

                                  return (
                                    <Box
                                      key={price.Id}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        p: 1.5,
                                        bgcolor: alpha(
                                          theme.palette.background.default,
                                          0.6
                                        ),
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                      }}
                                    >
                                      {/* PRICE ROW */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                        }}
                                      >
                                        <SellOutlinedIcon
                                          fontSize="small"
                                          sx={{
                                            color: "text.secondary",
                                            opacity: 0.5,
                                          }}
                                        />

                                        <TextField
                                          select
                                          size="small"
                                          value={
                                            price.AmountAdjustmentType ||
                                            "price-add"
                                          }
                                          sx={{ width: 180 }}
                                          onChange={(e) =>
                                            handleUpdatePrice(
                                              attr.Id,
                                              opt.Id,
                                              price.Id,
                                              "AmountAdjustmentType",
                                              e.target.value
                                            )
                                          }
                                        >
                                          <MenuItem value="price-add">
                                            Add Price (+)
                                          </MenuItem>
                                          <MenuItem value="price-minus">
                                            Subtract Price (-)
                                          </MenuItem>
                                          <MenuItem value="percentage-add">
                                            Add Percent (+%)
                                          </MenuItem>
                                          <MenuItem value="percentage-minus">
                                            Subtract Percent (-%)
                                          </MenuItem>
                                        </TextField>

                                        <TextField
                                          size="small"
                                          type="number"
                                          label="Amount"
                                          value={
                                            price.AmountLocalSubTotal || ""
                                          }
                                          sx={{ width: 120 }}
                                          onChange={(e) =>
                                            handleUpdatePrice(
                                              attr.Id,
                                              opt.Id,
                                              price.Id,
                                              "AmountLocalSubTotal",
                                              e.target.value
                                            )
                                          }
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                {price.AmountAdjustmentType?.includes(
                                                  "percent"
                                                )
                                                  ? "%"
                                                  : "£"}
                                              </InputAdornment>
                                            ),
                                          }}
                                        />

                                        <Box
                                          sx={{
                                            flexGrow: 1,
                                            p: 0.5,
                                            bgcolor: alpha(
                                              theme.palette.success.main,
                                              0.1
                                            ),
                                            borderRadius: 1,
                                            textAlign: "center",
                                            minWidth: 100,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            color="success.dark"
                                          >
                                            Preview:{" "}
                                            {calculatePreview(
                                              price.AmountAdjustmentType,
                                              price.AmountLocalSubTotal
                                            )}
                                          </Typography>
                                        </Box>

                                        <Tooltip title="Schedule Price">
                                          <IconButton
                                            size="small"
                                            color={
                                              showPriceSchedule
                                                ? "secondary"
                                                : "default"
                                            }
                                            onClick={() =>
                                              handleToggleScheduleView(
                                                `price-sch-${price.Id}`
                                              )
                                            }
                                          >
                                            {showPriceSchedule ? (
                                              <EventAvailableRoundedIcon fontSize="small" />
                                            ) : (
                                              <EventRoundedIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </Tooltip>

                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeletePrice(
                                              attr.Id,
                                              opt.Id,
                                              price.Id
                                            )
                                          }
                                        >
                                          <DeleteRoundedIcon
                                            fontSize="small"
                                            color="error"
                                          />
                                        </IconButton>
                                      </Box>

                                      {/* PRICE SCHEDULE */}
                                      <Collapse in={showPriceSchedule}>
                                        <Box
                                          sx={{
                                            ml: 4,
                                            mt: 1,
                                            pl: 2,
                                            borderLeft: `2px solid ${theme.palette.secondary.light}`,
                                          }}
                                        >
                                          <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                type="datetime-local"
                                                label="Price Start Time"
                                                value={
                                                  price.StartDateTime || ""
                                                }
                                                onChange={(e) =>
                                                  handleUpdatePrice(
                                                    attr.Id,
                                                    opt.Id,
                                                    price.Id,
                                                    "StartDateTime",
                                                    e.target.value
                                                  )
                                                }
                                                InputLabelProps={{
                                                  shrink: true,
                                                }}
                                              />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                type="datetime-local"
                                                label="Price End Time"
                                                value={price.EndDateTime || ""}
                                                onChange={(e) =>
                                                  handleUpdatePrice(
                                                    attr.Id,
                                                    opt.Id,
                                                    price.Id,
                                                    "EndDateTime",
                                                    e.target.value
                                                  )
                                                }
                                                InputLabelProps={{
                                                  shrink: true,
                                                }}
                                              />
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                }
                              )}
                            </Stack>
                          </Collapse>

                          {/* ADD PRICE BUTTON */}
                          <Box
                            sx={{
                              mt: 1.5,
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddCircleRoundedIcon />}
                              sx={{
                                borderRadius: 6,
                                textTransform: "none",
                                fontSize: "0.75rem",
                              }}
                              onClick={() => handleAddPrice(attr.Id, opt.Id)}
                            >
                              Add Price Modifier
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Divider sx={{ my: 3 }} />

                  {/* ACTION PANEL */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      startIcon={<AddCircleRoundedIcon />}
                      size="small"
                      onClick={() => handleAddOption(attr.Id)}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Add preset option
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      disableElevation
                      startIcon={
                        isCurrentlySaving ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SaveRoundedIcon />
                        )
                      }
                      onClick={() => handleSaveAttribute(attr.Id)}
                      disabled={isSaveDisabled}
                      sx={{ textTransform: "none", fontWeight: 600, px: 4 }}
                    >
                      {isCurrentlySaving
                        ? "Saving..."
                        : isNewRecord
                        ? "Save New Attribute"
                        : "Update Attribute"}
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* DELETE MODAL */}
      <DeleteModal
        show={deleteModalShow}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={() => {
          setDeleteModalShow(false);
          setAttributeToDelete(null);
        }}
        customText="Are you sure you want to permanently delete this attribute and all of its options?"
      />
    </Box>
  );
}
